import styled from 'libs/styled';
import { useTranslation } from 'react-i18next';
import { above } from '@sportson/core-web/utils/mediaqueries';
import decodeHTMLEntities from '@sportson/core-web/utils/decodeHTMLEntities';

import Link from '@sportson/core-web/components/Link';
import Text from 'components/Text';
import { ArrowIcon } from 'assets/media/icons/ArrowIcon';
import Image from '@sportson/core-web/components/Image';
import { STORM_IMAGE_URL } from 'config/constants';

const CardLink = styled(Link)`
    width: 100%;
    display: flex;
    align-items: flex-start;
    flex-shrink: 0;
    border-bottom: 1px solid var(--color-neutrals-200);

    .hoverText {
        opacity: 0;
        transition: all var(--transition-primary-fast);
    }

    ${above['tablet.lg']} {
        &:hover .hoverText {
            opacity: 1;
        }
    }
`;

const ImageWrapper = styled('div')`
    min-width: 104px;
    max-width: 104px;
    aspect-ratio: 1/1;
    overflow: hidden;
    border-radius: 4px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const TextWrapper = styled('div')`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export interface ArticleCollectionCardProps {
    url: string;
    tag: string;
    title: string;
    image: {
        src: string;
        alt?: string;
    };
}

export const ArticleCollectionCard = ({ url, tag, title, image }) => {
    const { t } = useTranslation();
    const imageSrc = image?.src || `${STORM_IMAGE_URL}/${image}.jpg` || '';
    const widths = [100, null, null, 150];

    if (!url) {
        return null;
    }

    return (
        <CardLink
            to={url}
            ariaLabel={t('articles.navigate_to_article', { articleTitle: title })}
            gap={['12px', null, null, null, '24px']}
            p={['16px 0', null, null, null, '24px 0']}
        >
            <ImageWrapper>
                {!imageSrc ? (
                    <Text typography="UI/12_100_0_350">{t('image_not_available')}</Text>
                ) : (
                    <Image src={{ url: imageSrc, width: widths }} alt={image.alt} title={title} />
                )}
            </ImageWrapper>
            <TextWrapper>
                {tag && (
                    <Text typography="UI/12_100_0_450_uppercase" color="var(--color-neutrals-500)">
                        {tag}
                    </Text>
                )}
                {title && (
                    // TODO: NOTE: Add proper typography, missing variable in design
                    <Text typography="Content/16_130_0_500" color="var(--color-base-black)">
                        {decodeHTMLEntities(title)}
                    </Text>
                )}
                <Text
                    className="hoverText"
                    typography="UI/12_100_0_450_uppercase"
                    color="var(--color-neutrals-500)"
                    as="span"
                    display="flex"
                    alignItems="center"
                    gap="12px"
                    marginTop="auto"
                >
                    {t('articles.read_article_collection')}
                    <ArrowIcon size="xsmall" />
                </Text>
            </TextWrapper>
        </CardLink>
    );
};
